import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Plugin } from '@tiptap/pm/state';
import { readRichText } from '../../../../../common/richTextDocument';

export const EMPTY_DOCUMENT = { type: 'doc', content: [{ type: 'paragraph' }] };
export const LIMIT_MESSAGE =
  'That change exceeds the article limits (20,000 characters or too much formatting). Shorten the content and try again. Your previous content is unchanged.';

export const safeLink = (href) =>
  !readRichText(
    JSON.stringify({
      version: 1,
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'Link',
              marks: [{ type: 'link', attrs: { href } }]
            }
          ]
        }
      ]
    })
  ).error;

// Drop editor-only default attributes at the storage boundary, not document content.
export const serializeDocument = (document) => {
  const copy = (node) => {
    const result = { type: node.type };
    if (node.type === 'heading') result.attrs = { level: node.attrs.level };
    if (node.text !== undefined) result.text = node.text;
    if (node.marks?.length)
      result.marks = node.marks.map((mark) =>
        mark.type === 'link'
          ? { type: 'link', attrs: { href: mark.attrs.href } }
          : { type: mark.type }
      );
    if (node.content) result.content = node.content.map(copy);
    return result;
  };
  const serialized = JSON.stringify({ ...copy(document), version: 1 });
  const result = readRichText(serialized);
  if (result.error) throw new Error(LIMIT_MESSAGE);
  return {
    value: result.document ? serialized : null,
    characters: result.characters || 0
  };
};

// Paste HTML is parsed in an inert template and reduced to our supported grammar.
// Nothing from this template is mounted; ProseMirror parses the cleaned markup.
export const cleanPastedHTML = (html) => {
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content
    .querySelectorAll(
      'script,style,iframe,object,embed,img,svg,math,video,audio,input,button,textarea,select'
    )
    .forEach((node) => node.remove());
  const allowed = new Set([
    'P',
    'DIV',
    'H1',
    'H2',
    'UL',
    'OL',
    'LI',
    'BR',
    'A',
    'STRONG',
    'B',
    'EM',
    'I',
    'U'
  ]);
  template.content.querySelectorAll('*').forEach((node) => {
    const href = node.getAttribute('href');
    const bold = /^(bold|[6-9]00)$/.test(node.style.fontWeight);
    const italic = node.style.fontStyle === 'italic';
    const underline = node.style.textDecoration.includes('underline');
    Array.from(node.attributes).forEach((attr) =>
      node.removeAttribute(attr.name)
    );
    if (node.tagName === 'A' && safeLink(href)) node.setAttribute('href', href);
    for (const tag of [
      bold && 'strong',
      italic && 'em',
      underline && 'u'
    ].filter(Boolean)) {
      const wrapper = document.createElement(tag);
      wrapper.append(...node.childNodes);
      node.append(wrapper);
    }
    if (!allowed.has(node.tagName)) {
      // Keep block boundaries from unsupported headings/tables, but not their formatting.
      if (/^(H[3-6]|TR|BLOCKQUOTE|PRE)$/.test(node.tagName))
        node.append(document.createElement('br'));
      node.replaceWith(...node.childNodes);
    }
  });
  return template.innerHTML;
};

export const richTextExtensions = (onRejected = () => {}) => [
  StarterKit.configure({
    heading: { levels: [1, 2] },
    blockquote: false,
    code: false,
    codeBlock: false,
    strike: false,
    horizontalRule: false,
    trailingNode: false,
    link: {
      openOnClick: false,
      autolink: false,
      linkOnPaste: false,
      isAllowedUri: safeLink
    }
  }),
  Extension.create({
    name: 'richTextLimits',
    addProseMirrorPlugins() {
      return [
        new Plugin({
          // Version 1 always displays decimal lists starting at one, including input rules.
          appendTransaction(transactions, _previous, state) {
            if (!transactions.some((transaction) => transaction.docChanged))
              return null;
            const transaction = state.tr;
            state.doc.descendants((node, position) => {
              if (
                node.type.name === 'orderedList' &&
                (node.attrs.start !== 1 || node.attrs.type !== null)
              ) {
                transaction.setNodeMarkup(position, undefined, {
                  ...node.attrs,
                  start: 1,
                  type: null
                });
              }
            });
            return transaction.docChanged ? transaction : null;
          },
          filterTransaction(transaction) {
            if (!transaction.docChanged) return true;
            try {
              serializeDocument(transaction.doc.toJSON());
              return true;
            } catch {
              onRejected(LIMIT_MESSAGE);
              return false;
            }
          }
        })
      ];
    }
  })
];
